// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  Text,
  TextInput,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"

import { Ionicons } from "@expo/vector-icons"
import { useNavigation } from "@react-navigation/native"
import { useQueryClient } from "@tanstack/react-query"
import { KeyboardAwareScrollView } from "react-native-keyboard-controller"
import { MemberFactField } from "types/sponsor"

import { PRIVACY_POLICY_URL } from "src/app/constants"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useMemberFactFields } from "src/queries/useConfig"
import { useMember, useUpdateMemberFacts } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"
import { Button } from "./Ignite/Button"

const apiUnknownValueDisplayText = ""
const clearStringFieldValue = ""
const pollTimeForFacts = 10_000

interface MemberFactsContentProps {
  /** Optional callback when navigating away (used for saving edits) */
  _onNavigateAway?: () => void
}

/**
 * MemberFactsContent - The core profile editing content.
 * Use this when embedding in SidekickView or other custom layouts.
 */
export const MemberFactsContent = ({ _onNavigateAway }: MemberFactsContentProps = {}) => {
  const { themed } = useAppTheme()
  const queryClient = useQueryClient()
  const member = useMember()
  const apiMemberFactFields = useMemberFactFields()
  const updateMemberFacts = useUpdateMemberFacts()
  const facts = useMemo(() => member?.facts || {}, [member?.facts])
  const approvedFacts = useMemo(() => member?.approved_facts || [], [member?.approved_facts])
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingFieldValue, setEditingFieldValue] = useState<string | null>(null)
  // These are values edited since we've loaded the profile, for visual distinction until navigation away
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})
  // Values that are currently being updated with the backend
  const [updatingValues, setUpdatingValues] = useState<Record<string, string>>({})

  const isAnyFieldUpdating = Object.keys(updatingValues).length > 0

  useEffect(() => {
    // Refresh on first mount
    queryClient.invalidateQueries({ queryKey: ["member"] })

    // Refresh periodically
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["member"] })
    }, pollTimeForFacts)

    return () => clearInterval(interval)
  }, [queryClient])

  const isApiFactEmpty = (value?: string) => {
    if (value === undefined || value === null) {
      return true
    }

    return value === "" || value === "UNKNOWN" || value.includes("No extractable information")
  }

  const normalizeStringFact = (value?: string): string => {
    const v = value || ""
    return isApiFactEmpty(v) ? clearStringFieldValue : v
  }

  function normalizeApiValues(): Record<string, string> {
    const stringFields = sortedFields.filter((field) => field.field_type === "string")
    const dateFields = sortedFields.filter((field) => field.field_type === "date")
    const optionsFields = sortedFields.filter((field) => field.field_type === "enum")

    const stringApiValues = Object.fromEntries(
      stringFields.map((field) => [field.name, normalizeStringFact(facts[field.name])]),
    )

    const dateApiValues = Object.fromEntries(
      dateFields.map((field) => [field.name, normalizeStringFact(facts[field.name])]),
    )

    const optionApiValues = Object.fromEntries(
      optionsFields.map((field) => [field.name, facts[field.name]]),
    )

    const apiValues = {
      ...stringApiValues,
      ...dateApiValues,
      ...optionApiValues,
    }

    return apiValues
  }

  const isEditedField = (key: string) => {
    return editedValues[key] !== undefined
  }

  const isEditingThisField = (key: string) => {
    return editingField === key
  }

  const isFieldUpdating = (key: string) => {
    return updatingValues[key] !== undefined
  }

  const fieldNeedsApproval = React.useCallback(
    (key: string) => {
      // If api fact is empty then nothing was extracted, no need to approve that (unless we implement fact history)
      return !(approvedFacts as string[]).includes(key) && !isApiFactEmpty(facts[key])
    },
    [approvedFacts, facts],
  )

  // FIXME: we use negative fact_order to indicate a disabled field. Remove when this hack is fixed.
  const enabledFields = apiMemberFactFields.filter((field) => field.fact_order > -1)
  const sortedFields = enabledFields.sort((a, b) => a.fact_order - b.fact_order)
  const apiValues = normalizeApiValues()
  const currentValues = Object.fromEntries(
    Object.entries(apiValues).map(([key, value]) => [key, editedValues[key] || value]),
  )

  const displayNameForApiField = (apiFieldName: string) => {
    return apiFieldName.replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const displayTextForStringValue = (value: string) => {
    return value === clearStringFieldValue ? apiUnknownValueDisplayText : value
  }

  const startEditField = (key: string, value: string) => {
    setEditingField(key)
    setEditingFieldValue(value)
  }

  const saveField = React.useCallback(
    async (key: string, value: string) => {
      // Set the updating value before mutation so that it's the display text during the loading time
      setUpdatingValues((prev) => ({ ...prev, [key]: value }))

      try {
        await updateMemberFacts.mutateAsync({ [key]: value })
        setEditedValues((prev) => ({ ...prev, [key]: value }))
      } catch (error) {
        console.error("acceptFieldEdit: Failed to update member fact:", error)
      } finally {
        setUpdatingValues((prev) => {
          const { [key]: _, ...rest } = prev
          return rest
        })
      }
    },
    [updateMemberFacts],
  )

  const acceptFieldEdit = React.useCallback(
    (key: string) => {
      console.log("[MemberFacts] acceptFieldEdit", key, editingField, editingFieldValue)
      if (editingField) {
        saveField(key, editingFieldValue || clearStringFieldValue)
        setEditingField(null)
        setEditingFieldValue(null)
      } else if (fieldNeedsApproval(key)) {
        saveField(key, currentValues[key])
      }
    },
    [currentValues, editingField, editingFieldValue, fieldNeedsApproval, saveField],
  )

  const rejectFieldEdit = (key: string) => {
    if (editingField) {
      setEditingField(null)
      setEditingFieldValue(null)
    } else if (fieldNeedsApproval(key)) {
      saveField(key, clearStringFieldValue)
    }
  }

  const handleOptionSelect = async (key: string, value: string) => {
    setUpdatingValues((prev) => ({ ...prev, [key]: value }))
    if (!member) {
      console.error("handleOptionSelect: Member is undefined, cannot update facts.")
      return
    }

    try {
      const allFacts = member.facts
      await updateMemberFacts.mutateAsync({ ...allFacts, [key]: value })
      setEditedValues((prev) => ({ ...prev, [key]: value }))
    } catch (error) {
      console.error("handleOptionSelect: Failed to update option:", error)
    } finally {
      setUpdatingValues((prev) => {
        const { [key]: _, ...rest } = prev
        return rest
      })
    }
  }

  const acceptRejectIcons = (key: string) => {
    const iconSize = 20

    return (
      <View style={themed($inputIcons)}>
        <Pressable onPress={() => acceptFieldEdit(key)} style={themed($iconButtonPressable)}>
          {({ pressed }) => (
            <View style={[themed($iconButton), pressed && themed($pressed)]}>
              <Ionicons name="checkmark" size={iconSize} style={themed($iconColor)} />
            </View>
          )}
        </Pressable>
        <Pressable onPress={() => rejectFieldEdit(key)} style={themed($iconButtonPressable)}>
          {({ pressed }) => (
            <View style={[themed($iconButton), pressed && themed($pressed)]}>
              <Ionicons name="close" size={iconSize} style={themed($iconColor)} />
            </View>
          )}
        </Pressable>
      </View>
    )
  }

  const handleFieldFocus = (key: string, value: string) => {
    if (!isEditingThisField(key)) {
      startEditField(key, value)
    }
  }

  const handleFieldBlur = React.useCallback(
    (key: string) => {
      console.log("[MemberFacts] handleFieldBlur", key, editingField, editingFieldValue)
      acceptFieldEdit(key)
    },
    [acceptFieldEdit, editingField, editingFieldValue],
  )

  // Placeholder for future navigation callback handling
  // The _onNavigateAway prop can be used to save edits when navigating away

  const loadingIcon = () => {
    return <ActivityIndicator size="small" style={themed($iconLoading)} />
  }

  const getMaxLength = (field: MemberFactField): number => {
    if (field.fact_response_length === "long") return 5000
    return 100
  }

  const editableTextField = (field: MemberFactField, value: string) => {
    const key = field.name
    const maxLength = getMaxLength(field)
    const editingStyle =
      isEditedField(key) || isEditingThisField(key) ? themed($editedText) : themed($apiText)
    let displayValue: string
    if (isEditingThisField(key)) {
      displayValue = editingFieldValue || clearStringFieldValue
    } else if (isFieldUpdating(key)) {
      displayValue = updatingValues[key]
    } else {
      displayValue = displayTextForStringValue(value)
    }

    const charCount = isEditingThisField(key)
      ? (editingFieldValue || "").length
      : displayValue.length
    const atMax = charCount >= maxLength

    return (
      <View>
        <TextInput
          value={displayValue}
          onChangeText={(text: string) => {
            if (isEditingThisField(key)) {
              setEditingFieldValue(text)
            }
          }}
          onBlur={() => handleFieldBlur(key)}
          onFocus={() => handleFieldFocus(key, displayValue)}
          style={[themed($textInput), editingStyle]}
          multiline={true}
          maxLength={maxLength}
        />
        {isEditingThisField(key) && (
          <Text style={[themed($charCounter), atMax && themed($charCounterAtMax)]}>
            {charCount}/{maxLength} Characters
          </Text>
        )}
      </View>
    )
  }

  const renderOptionButtons = (field: MemberFactField) => {
    const key = field.name
    const currentValue = currentValues[key]
    const pendingValue = updatingValues[key]
    const optionValues = field.enums || []

    return (
      <View style={themed($buttonGroup)}>
        {optionValues.map((value: string, index: number) => {
          const isSelected = pendingValue ? value === pendingValue : value === currentValue

          return (
            <Button
              key={index}
              text={value}
              onPress={() => {
                Keyboard.dismiss()
                handleOptionSelect(key, value)
              }}
              style={[
                themed($buttonGroupButton),
                index === optionValues.length - 1 && themed($buttonGroupButtonLast),
                isSelected && themed($buttonGroupButtonSelected),
              ]}
              textStyle={[
                themed($buttonText),
                isSelected && themed($buttonGroupButtonSelectedText),
              ]}
              disabled={isAnyFieldUpdating}
            />
          )
        })}
      </View>
    )
  }

  const renderField = (field: MemberFactField, value: string) => {
    const fieldName = field.name
    const label = displayNameForApiField(field.name)
    const factType = field.fact_type
    const fieldType = field.field_type

    const showAcceptReject = fieldNeedsApproval(fieldName)
    const showLoading = isFieldUpdating(fieldName)

    // Groups and headers are are stored along with fields, but we want to render them differently
    // Anything else is rendered as an input field.
    if (factType === "group" || factType === "header") {
      return (
        <View key={fieldName}>
          <Text style={themed($groupTitle)}>{label}</Text>
        </View>
      )
    } else if (fieldType === "enum") {
      return (
        <View key={fieldName} style={themed($field)}>
          <View style={themed($fieldLabelRow)}>
            <Text style={themed($fieldLabel)}>{label}</Text>
            <View style={themed($fieldIconsContainer)}>
              {showAcceptReject && acceptRejectIcons(fieldName)}
            </View>
          </View>
          {renderOptionButtons(field)}
        </View>
      )
    } else {
      // Render string, date, and unknown field types as editable text fields
      return (
        <View key={fieldName} style={themed($field)}>
          <View style={themed($fieldLabelRow)}>
            <Text style={themed($fieldLabel)}>{label}</Text>
            <View style={themed($fieldIconsContainer)}>
              {showLoading && loadingIcon()}
              {showAcceptReject && acceptRejectIcons(fieldName)}
            </View>
          </View>
          {editableTextField(field, value)}
        </View>
      )
    }
  }

  return (
    <KeyboardAwareScrollView
      style={themed($container)}
      contentContainerStyle={[
        themed($contentContainer),
        { paddingBottom: 120 }, // Space for ButtonCluster when used in SidekickView
      ]}
      // Prevents scroll position from jumping to position where it started editing on dismissal
      disableScrollOnKeyboardHide={true}
      // Prevents the scrollview from blocking taps into child components that might handle them
      // This allows non-text fields to be tapped even when the keyboard is visible
      keyboardShouldPersistTaps="handled"
    >
      {sortedFields.map((field) => (
        <View key={field.name}>{renderField(field, currentValues[field.name])}</View>
      ))}
      <Pressable
        onPress={() => openLinkInBrowser(PRIVACY_POLICY_URL)}
        style={themed($privacyLink)}
        accessibilityRole="link"
        accessibilityLabel="Privacy Policy"
      >
        <Text style={themed($privacyLinkText)}>Privacy Policy</Text>
      </Pressable>
    </KeyboardAwareScrollView>
  )
}

/**
 * MemberFacts - Wrapper component for standalone use with navigation header.
 * Uses the native navigation header with a Save button.
 */
export const MemberFacts = () => {
  const { themed } = useAppTheme()
  const navigation = useNavigation()

  // Set up the Save button in the navigation header
  React.useEffect(() => {
    const SaveButton = () => (
      <Pressable onPress={() => navigation.goBack()} style={themed($saveButtonPressable)}>
        <Text style={themed($saveButtonText)}>Save</Text>
      </Pressable>
    )

    navigation.setOptions({
      headerRight: () => <SaveButton />,
    })
  }, [navigation, themed])

  return <MemberFactsContent />
}

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 1,
  backgroundColor: colors.background,
  padding: spacing.md,
})

const $editedText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
})

const $apiText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
})

const $field: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginBottom: spacing.md,
})

const $groupTitle: ThemedStyle<TextStyle> = ({ colors, typography, spacing }) => ({
  color: colors.text,
  fontFamily: typography.fonts.figtree.semiBold,
  fontSize: 20,
  marginVertical: spacing.md,
  paddingHorizontal: spacing.xs,
})

const $fieldLabel: ThemedStyle<TextStyle> = ({ colors, typography, spacing }) => ({
  color: colors.text,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 16,
  paddingHorizontal: spacing.sm,
  marginBottom: 10,
  flex: 1,
})

const $buttonGroup: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flexDirection: "row",
  borderRadius: spacing.sm,
  overflow: "hidden",
  backgroundColor: colors.buttonBackground,
})

const $buttonGroupButton: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 1,
  paddingVertical: spacing.xs,
  backgroundColor: colors.buttonBackground,
  borderRadius: 0,
  borderWidth: 0,
  borderRightWidth: 1,
  borderRightColor: colors.background,
  marginLeft: 0,
  marginRight: 0,
})

const $buttonGroupButtonLast: ThemedStyle<ViewStyle> = () => ({
  borderRightWidth: 0,
})

const $buttonGroupButtonSelected: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.white,
})

const $buttonGroupButtonSelectedText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.palette.neutral800,
})

const $buttonText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.buttonText,
  fontSize: 14,
  fontFamily: typography.fonts.figtree.normal,
  textAlign: "center",
})

const $textInput: ThemedStyle<TextStyle> = ({ colors, typography, spacing }) => ({
  flex: 1,
  color: colors.inputText,
  padding: spacing.md,
  borderRadius: spacing.sm,
  backgroundColor: colors.inputBackground,
  borderWidth: 2,
  borderColor: colors.inputBorder,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 14,
})

const $inputIcons: ThemedStyle<ViewStyle> = () => ({
  flexDirection: "row",
  height: "100%",
})

const $iconColor: ThemedStyle<{ color: string }> = ({ colors }) => ({
  color: colors.textDim,
})

const $iconButtonPressable: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  justifyContent: "center",
  paddingLeft: spacing.xs,
  paddingRight: spacing.xs,
})

const $iconButton: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  borderRadius: spacing.xxs * 2,
  padding: spacing.xxs,
  alignItems: "center",
  justifyContent: "center",
})

const $iconLoading: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
})

const $pressed: ThemedStyle<ViewStyle> = () => ({
  opacity: 0.5,
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingBottom: spacing.xl,
})

const $fieldLabelRow: ThemedStyle<ViewStyle> = ({ spacing }) => {
  // We want the field label to be the same height as the text input when it's one line
  // FIXME: better way to share these so you can have trees of computed layout props?
  // You need to be in a component to get the computed theme, are we meant to only derive from theme values?
  const textInputFontsize = 14
  const textInputPadding = spacing.md
  const rowHeight = textInputFontsize + textInputPadding * 2

  return {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: rowHeight,
  }
}

const $fieldIconsContainer: ThemedStyle<ViewStyle> = () => ({
  flexDirection: "row",
  height: "100%",
  marginBottom: 10,
  flexShrink: 0,
})

const $saveButtonPressable: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingHorizontal: spacing.md,
  justifyContent: "center",
  alignItems: "center",
})

const $saveButtonText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.primary || colors.text,
  fontSize: 16,
  fontFamily: typography.fonts.figtree.medium,
})

const $charCounter: ThemedStyle<TextStyle> = ({ colors, typography, spacing }) => ({
  color: colors.textDim,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 12,
  textAlign: "right",
  marginTop: spacing.xxs,
  paddingHorizontal: spacing.xs,
})

const $charCounterAtMax: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.error,
})

const $privacyLink: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  paddingVertical: spacing.md,
})

const $privacyLinkText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.textDim,
  fontFamily: typography.fonts.figtree.normal,
  fontSize: 14,
  textDecorationLine: "underline",
})
